import React, { useEffect, useRef, useState } from "react";
import { Link, NavLink, useLocation, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import {
  ChevronDown, Menu, Moon, Search, ShoppingBag, Sun, User, X,
} from "lucide-react";
import { Container } from "./Primitives";
import { useCurrency } from "../lib/currency";
import { useCart } from "../lib/cart";
import { useTheme } from "../lib/theme";
import { getMukhiAvailability } from "../lib/api";
import { MuhuratRibbon } from "./MuhuratRibbon";
import {
  AnimatePresence, drawerLeft, menuDrop, motion, scrim, staggerParent, rise,
} from "../lib/motion";
import {
  BUDGET_NAV, CATEGORY_NAV, CONSULT_NAV, INTENT_NAV, PLANET_NAV, STONE_NAV,
} from "../content/nav";

const ANNOUNCEMENTS = [
  "Free insured shipping worldwide · Every stone verified on the laboratory's own website · 30-day returns",
  "Prices include tax. Certification and energisation are included, never an upgrade.",
  "Diwali orders: place by 1 November for delivery in India, 26 October for USA and UK.",
];

export function AnnouncementBar() {
  const [dismissed, setDismissed] = useState(false);
  const [i, setI] = useState(0);

  useEffect(() => {
    const t = setInterval(() => setI((v) => (v + 1) % ANNOUNCEMENTS.length), 7000);
    return () => clearInterval(t);
  }, []);

  if (dismissed) return null;
  return (
    <div className="bg-gold text-white dark:text-[#141310]" data-testid="announcement-bar" style={{ minHeight: 34 }}>
      <Container className="flex items-center justify-between gap-4 py-2">
        <div className="relative flex-1 text-center">
          <AnimatePresence mode="wait">
            <motion.p
              key={i}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
              className="announce leading-snug"
              style={{ margin: 0 }}
            >
              {ANNOUNCEMENTS[i]}
            </motion.p>
          </AnimatePresence>
        </div>
        <button
          type="button"
          aria-label="Dismiss announcement"
          data-testid="announcement-dismiss"
          onClick={() => setDismissed(true)}
          className="opacity-60 transition-opacity hover:opacity-100"
        >
          <X className="h-3.5 w-3.5" />
        </button>
      </Container>
    </div>
  );
}

function MegaColumn({ title, children, className = "" }) {
  return (
    <div className={className}>
      <p className="menu-h">{title}</p>
      <ul className="space-y-0">{children}</ul>
    </div>
  );
}

function MegaLink({ to, children, onClick }) {
  return (
    <motion.li variants={rise}>
      <Link to={to} onClick={onClick} className="menu-link">
        {children}
      </Link>
    </motion.li>
  );
}

function MegaSheet({ children, testId }) {
  return (
    <motion.div
      data-testid={testId}
      variants={menuDrop}
      initial="hidden"
      animate="show"
      exit="exit"
      className="absolute left-0 right-0 top-full z-40 border-b border-rule bg-surface elev-lift"
    >
      <motion.div variants={staggerParent(0.03)} initial="hidden" animate="show">
        {children}
      </motion.div>
    </motion.div>
  );
}

function PlanetLine({ label }) {
  const [planet, stone] = label.split(" · ");
  return (
    <>
      <span className="menu-link__accent">{planet}</span>
      {stone ? <span className="opacity-60"> · {stone}</span> : null}
    </>
  );
}

function ShopMega({ close }) {
  const { data: mukhi } = useQuery({
    queryKey: ["mukhi-availability"], queryFn: getMukhiAvailability, staleTime: 60_000,
  });
  const mukhiRows = mukhi?.items || Array.from({ length: 14 }, (_, i) => ({ mukhi: i + 1, in_stock: true }));
  return (
    <MegaSheet testId="shop-mega-menu">
      <Container className="grid grid-cols-2 gap-6 py-12 lg:grid-cols-12">
        <MegaColumn title="By category" className="lg:col-span-2">
          {CATEGORY_NAV.map((c) => (
            <MegaLink key={c.to} to={c.to} onClick={close}>{c.label}</MegaLink>
          ))}
        </MegaColumn>

        <MegaColumn title="By planet" className="lg:col-span-2">
          {PLANET_NAV.map((p) => (
            <MegaLink key={p.slug} to={`/graha/${p.slug}`} onClick={close}>
              <PlanetLine label={p.label} />
            </MegaLink>
          ))}
        </MegaColumn>

        <MegaColumn title="By intent" className="lg:col-span-2">
          {INTENT_NAV.map((x) => (
            <MegaLink key={x.slug} to={`/shop/by-intent/${x.slug}`} onClick={close}>{x.label}</MegaLink>
          ))}
        </MegaColumn>

        <MegaColumn title="By budget" className="lg:col-span-2">
          {BUDGET_NAV.map((b) => (
            <MegaLink key={b.slug} to={`/shop/by-budget/${b.slug}`} onClick={close}>{b.label}</MegaLink>
          ))}
        </MegaColumn>

        <MegaColumn title="Gemstones" className="lg:col-span-2">
          {STONE_NAV.map((s) => (
            <MegaLink key={s.slug} to={`/shop/gemstones/${s.slug}`} onClick={close}>
              <span className="menu-link__accent">{s.name}</span>
              <span className="opacity-60"> · {s.hindi}</span>
            </MegaLink>
          ))}
          <li className="pt-2">
            <Link to="/shop/gemstones" onClick={close} className="ui link-u text-xs">
              View all gemstones →
            </Link>
          </li>
        </MegaColumn>

        <motion.div variants={rise} className="lg:col-span-2 lg:border-l lg:border-rule lg:pl-6">
          <p className="menu-h">Rudraksha by mukhi</p>
          <div className="grid grid-cols-7 gap-1.5">
            {mukhiRows.map((m) => {
              const cell = (
                <span
                  className={`num flex h-8 items-center justify-center border text-xs transition-colors duration-200 ${
                    m.in_stock
                      ? "border-rule text-dim hover:border-gold hover:text-gold"
                      : "border-rule/60 text-dim/40 cursor-not-allowed"
                  }`}
                >
                  {m.mukhi}
                </span>
              );
              return m.in_stock ? (
                <Link
                  key={m.mukhi}
                  to={`/shop/rudraksha/${m.mukhi}-mukhi`}
                  onClick={close}
                  data-testid={`mega-mukhi-${m.mukhi}`}
                >
                  {cell}
                </Link>
              ) : (
                <span
                  key={m.mukhi}
                  role="button"
                  aria-disabled="true"
                  title="None in stock right now"
                  data-testid={`mega-mukhi-${m.mukhi}-disabled`}
                >
                  {cell}
                </span>
              );
            })}
          </div>
          <div
            className="mt-6 border border-rule bg-wash/60 p-5"
            data-testid="mega-not-sure"
          >
            <p className="menu-h !mb-2 !pb-0 !border-b-0">Not sure?</p>
            <p className="ui mt-1 text-xs leading-[1.6] text-dim">
              A 20-minute session with a named consultant — <span className="num">₹499</span>,
              credited back against any purchase over ₹5,000.
            </p>
            <Link
              to="/consult/suitability-session"
              onClick={close}
              className="btn btn-primary mt-4 w-full !px-3 !text-[11px]"
              data-testid="mega-book-proof-first"
            >
              Book — ₹499
            </Link>
          </div>
        </motion.div>
      </Container>
    </MegaSheet>
  );
}

function ConsultMega({ close }) {
  return (
    <MegaSheet testId="consult-menu">
      <Container className="grid gap-12 py-12 md:grid-cols-[minmax(0,1.4fr)_minmax(0,1fr)]">
        <div>
          <p className="menu-h">Consultations — prices shown, because nobody else does</p>
          <ul className="space-y-0">
            {CONSULT_NAV.map((c) => (
              <motion.li key={c.to} variants={rise} className="border-b border-rule/60 last:border-b-0">
                <Link
                  to={c.to}
                  onClick={close}
                  className="ui group flex items-baseline justify-between gap-6 py-3 pr-1 text-[13px] text-ink transition-colors duration-200 hover:text-gold"
                >
                  <span className="truncate">{c.label}</span>
                  <span className="num shrink-0 text-[12px] text-dim transition-colors duration-200 group-hover:text-gold">
                    {c.price}
                  </span>
                </Link>
              </motion.li>
            ))}
          </ul>
          <Link
            to="/consult"
            onClick={close}
            data-testid="consult-mega-see-all"
            className="ui mt-4 inline-flex items-center gap-1.5 text-xs font-medium tracking-[0.08em] text-gold transition-colors duration-200 hover:text-deep"
          >
            View all consultations
            <span className="transition-transform duration-200 group-hover:translate-x-0.5">→</span>
          </Link>
        </div>
        <div className="md:border-l md:border-rule md:pl-10">
          <p className="menu-h">Before you book</p>
          <ul className="space-y-0">
            <MegaLink to="/about/consultants" onClick={close}>Our consultants</MegaLink>
            <MegaLink to="/promises" onClick={close}>The Guarantee Stack</MegaLink>
            <MegaLink to="/consult/scope-of-practice" onClick={close}>Scope of practice</MegaLink>
            <MegaLink to="/consult/if-you-are-in-crisis" onClick={close}>If you are in crisis</MegaLink>
          </ul>
        </div>
      </Container>
    </MegaSheet>
  );
}

function CurrencySelect({ testId = "currency-select" }) {
  const { code, currencies, setCurrency } = useCurrency();
  return (
    <label className="ui inline-flex items-center gap-1 text-xs text-dim">
      <span className="sr-only">Currency</span>
      <select
        data-testid={testId}
        value={code}
        onChange={(e) => setCurrency(e.target.value)}
        className="num cursor-pointer border border-rule bg-transparent px-2 py-1 text-xs text-ink transition-colors hover:border-gold"
      >
        {currencies.map((c) => (
          <option key={c.code} value={c.code}>{c.code}</option>
        ))}
      </select>
    </label>
  );
}

export function Header() {
  const [open, setOpen] = useState(null);
  const [drawer, setDrawer] = useState(false);
  const [q, setQ] = useState("");
  const [searchOpen, setSearchOpen] = useState(false);
  const { count } = useCart();
  const { theme, toggle } = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const wrap = useRef(null);

  useEffect(() => {
    setOpen(null);
    setDrawer(false);
    setSearchOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") { setOpen(null); setDrawer(false); }
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, []);

  const submitSearch = (e) => {
    e.preventDefault();
    if (!q.trim()) return;
    navigate(`/search?q=${encodeURIComponent(q.trim())}`);
    setQ("");
    setSearchOpen(false);
  };

  const navLink = ({ isActive }) =>
    `nav-link-sm relative whitespace-nowrap transition-colors duration-200 ${isActive ? "text-gold" : "text-ink hover:text-gold"}`;

  return (
    <header
      ref={wrap}
      className="sticky top-0 z-50 border-b border-rule/60 bg-wash/[0.97] backdrop-blur-xl supports-[backdrop-filter]:bg-wash/[0.88]"
    >
      <Container className="flex min-h-[68px] items-center gap-4">
        <button
          type="button"
          className="lg:hidden"
          aria-label="Open menu"
          data-testid="mobile-menu-button"
          onClick={() => setDrawer(true)}
        >
          <Menu className="h-5 w-5" />
        </button>

        <Link to="/" data-testid="site-logo" className="group flex items-baseline gap-2">
          <span className="deva text-xl leading-none text-gold transition-transform duration-500 group-hover:-translate-y-0.5">
            श्रीं
          </span>
          <span className="wordmark whitespace-nowrap">Shreem Mantra Energy</span>
        </Link>

        <nav className="ml-6 hidden items-center gap-6 lg:flex" aria-label="Main">
          <button
            type="button"
            data-testid="nav-shop"
            onClick={() => setOpen(open === "shop" ? null : "shop")}
            aria-expanded={open === "shop"}
            className="nav-link-sm flex items-center gap-1 text-ink transition-colors duration-200 hover:text-gold"
          >
            Shop
            <motion.span animate={{ rotate: open === "shop" ? 180 : 0 }} transition={{ duration: 0.24 }}>
              <ChevronDown className="h-3.5 w-3.5" />
            </motion.span>
          </button>
          <button
            type="button"
            data-testid="nav-consult"
            onClick={() => setOpen(open === "consult" ? null : "consult")}
            aria-expanded={open === "consult"}
            className="nav-link-sm flex items-center gap-1 text-ink transition-colors duration-200 hover:text-gold"
          >
            Consult
            <motion.span animate={{ rotate: open === "consult" ? 180 : 0 }} transition={{ duration: 0.24 }}>
              <ChevronDown className="h-3.5 w-3.5" />
            </motion.span>
          </button>
          <NavLink to="/tools/verify-a-certificate" className={navLink} data-testid="nav-verify">Verify</NavLink>
          <NavLink to="/guide" className={navLink} data-testid="nav-guides">Guides</NavLink>
          <NavLink to="/promises" className={({ isActive }) => `${navLink({ isActive })} hidden xl:inline-flex`} data-testid="nav-promises">The Twelve Promises</NavLink>
        </nav>

        <div className="ml-auto flex items-center gap-3.5">
          <form onSubmit={submitSearch} className="hidden items-center xl:flex">
            <label className="sr-only" htmlFor="site-search">Search</label>
            <input
              id="site-search"
              data-testid="search-input"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search stones, mukhi, report no."
              className="num w-64 border-b border-rule bg-transparent px-1 py-1.5 text-xs text-ink placeholder:text-dim focus:w-72 focus:border-gold"
              style={{ transition: "width 0.35s cubic-bezier(0.16,1,0.3,1), border-color 0.2s ease" }}
            />
            <button type="submit" aria-label="Search" data-testid="search-submit" className="ml-1.5 text-dim transition-colors hover:text-gold">
              <Search className="h-4 w-4" />
            </button>
          </form>
          <button
            type="button"
            className="xl:hidden"
            aria-label="Search"
            data-testid="mobile-search-toggle"
            onClick={() => setSearchOpen((s) => !s)}
          >
            <Search className="h-5 w-5" />
          </button>
          <div className="hidden sm:block"><CurrencySelect /></div>
          <button
            type="button"
            onClick={toggle}
            aria-label={theme === "dark" ? "Switch to light mode" : "Switch to dark mode"}
            data-testid="theme-toggle"
            className="text-dim transition-colors hover:text-gold"
          >
            <AnimatePresence mode="wait" initial={false}>
              <motion.span
                key={theme}
                initial={{ opacity: 0, rotate: -45 }}
                animate={{ opacity: 1, rotate: 0 }}
                exit={{ opacity: 0, rotate: 45 }}
                transition={{ duration: 0.24 }}
                className="block"
              >
                {theme === "dark" ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
              </motion.span>
            </AnimatePresence>
          </button>
          <Link to="/account" aria-label="Account" data-testid="nav-account" className="hidden text-dim transition-colors hover:text-gold sm:block">
            <User className="h-4 w-4" />
          </Link>
          <Link to="/cart" data-testid="nav-cart" className="relative text-ink transition-colors hover:text-gold" aria-label={`Cart, ${count} items`}>
            <ShoppingBag className="h-5 w-5" />
            <AnimatePresence>
              {count > 0 && (
                <motion.span
                  initial={{ scale: 0, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1 }}
                  exit={{ scale: 0, opacity: 0 }}
                  transition={{ duration: 0.26, ease: [0.22, 1, 0.36, 1] }}
                  className="num absolute -right-2 -top-2 flex h-4 min-w-4 items-center justify-center bg-gold px-1 text-[10px] font-semibold text-white dark:text-[#141310]"
                >
                  {count}
                </motion.span>
              )}
            </AnimatePresence>
          </Link>
        </div>
      </Container>

      <AnimatePresence>
        {searchOpen && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.28, ease: [0.22, 1, 0.36, 1] }}
            className="overflow-hidden xl:hidden"
          >
            <Container className="pb-3">
              <form onSubmit={submitSearch} className="flex gap-2">
                <input
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                  data-testid="mobile-search-input"
                  placeholder="Search stones, mukhi, report no."
                  className="field num"
                />
                <button type="submit" className="btn btn-primary">Go</button>
              </form>
            </Container>
          </motion.div>
        )}
      </AnimatePresence>

      <MuhuratRibbon />

      <AnimatePresence>
        {open === "shop" && <ShopMega key="shop" close={() => setOpen(null)} />}
        {open === "consult" && <ConsultMega key="consult" close={() => setOpen(null)} />}
      </AnimatePresence>

      <AnimatePresence>
        {drawer && (
          <div className="fixed inset-0 z-50 lg:hidden" data-testid="mobile-drawer">
            <motion.div
              variants={scrim}
              initial="hidden"
              animate="show"
              exit="exit"
              className="absolute inset-0 bg-ink/45 backdrop-blur-sm"
              onClick={() => setDrawer(false)}
            />
            <motion.div
              variants={drawerLeft}
              initial="hidden"
              animate="show"
              exit="exit"
              className="absolute left-0 top-0 h-full w-[88%] max-w-sm overflow-y-auto border-r border-rule bg-wash p-7"
            >
              <div className="mb-7 flex items-center justify-between">
                <CurrencySelect testId="currency-select-mobile" />
                <button type="button" aria-label="Close menu" data-testid="mobile-drawer-close" onClick={() => setDrawer(false)}>
                  <X className="h-5 w-5" />
                </button>
              </div>
              <motion.nav variants={staggerParent(0.04)} initial="hidden" animate="show" className="space-y-8">
                <div>
                  <p className="eyebrow mb-3">Shop</p>
                  <ul className="space-y-2">
                    {CATEGORY_NAV.map((c) => (
                      <MegaLink key={c.to} to={c.to} onClick={() => setDrawer(false)}>{c.label}</MegaLink>
                    ))}
                  </ul>
                </div>
                <div>
                  <p className="eyebrow mb-3">Gemstones</p>
                  <ul className="space-y-2">
                    {STONE_NAV.map((s) => (
                      <MegaLink key={s.slug} to={`/shop/gemstones/${s.slug}`} onClick={() => setDrawer(false)}>
                        {s.name} ({s.hindi})
                      </MegaLink>
                    ))}
                  </ul>
                </div>
                <div>
                  <p className="eyebrow mb-3">Consult</p>
                  <ul className="space-y-2">
                    {CONSULT_NAV.map((c) => (
                      <MegaLink key={c.to} to={c.to} onClick={() => setDrawer(false)}>
                        {c.label} — <span className="num">{c.price}</span>
                      </MegaLink>
                    ))}
                  </ul>
                </div>
                <div>
                  <p className="eyebrow mb-3">More</p>
                  <ul className="space-y-2">
                    <MegaLink to="/promises" onClick={() => setDrawer(false)}>The Twelve Promises</MegaLink>
                    <MegaLink to="/tools" onClick={() => setDrawer(false)}>Free tools</MegaLink>
                    <MegaLink to="/about/scoreboard" onClick={() => setDrawer(false)}>Our scoreboard</MegaLink>
                    <MegaLink to="/guide" onClick={() => setDrawer(false)}>Guides</MegaLink>
                    <MegaLink to="/about" onClick={() => setDrawer(false)}>About</MegaLink>
                    <MegaLink to="/contact" onClick={() => setDrawer(false)}>Contact</MegaLink>
                  </ul>
                </div>
              </motion.nav>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </header>
  );
}
